'use client';

import Image from 'next/image';
import Link from 'next/link';
import React, { ReactElement, RefCallback } from 'react';

import { Color, ColorSize } from '@bytel/product-wall';
import {
    Align,
    Button,
    ButtonVariant,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Justify,
    Link as LinkTrilogy,
    Price,
    PriceLevel,
    Row,
    Rows,
    Title,
    TitleLevels,
    TypographyAlign,
    View,
} from '@bytel/trilogy-react';

import { getAppConfig } from '@services/config';

import { EnergyLabel } from '@components/energy-label';
import { ImageThumbor } from '@components/image-thumbor';
import { ReparabilityNote } from '@components/reparability';

import { paths } from '@helpers/path';

import { ComparatorCell } from '@comparator/components';

import { MobileProductsCharacteristicsType } from './mobile-comparator-service';
import { sendBackToPreviousPageTracking } from './tracking';

export type MobileCharacteristicGroup = {
    name: string;
    characteristics: MobileCharacteristic[];
};

export type MobileCharacteristic =
    | {
          name: string;
          values: (string | boolean | ReactElement | undefined)[];
      }
    | ReactElement;

const appConfig = getAppConfig();

export function buildCharacteristicGroups(
    isRenewal: boolean,
    products: MobileProductsCharacteristicsType,
    onRemoveProduct?: (gencode: string) => void,
    tableHeaderRef?: RefCallback<HTMLElement>,
): MobileCharacteristicGroup[] {
    return [
        {
            name: '',
            characteristics: [
                ...products.map((p, index) => (
                    <ComparatorCell key={p.name} className='has-text-centered'>
                        <Rows gap={GapSize.THREE}>
                            <Row ref={index === 0 ? tableHeaderRef : undefined} className='is-relative'>
                                <Icon
                                    name={IconName.TIMES}
                                    size={IconSize.SMALL}
                                    className='is-absolute-top-right cursor-pointer'
                                    onClick={() => onRemoveProduct?.(p.gencode)}
                                />
                                <Title level={TitleLevels.FIVE} typo={TypographyAlign.TEXT_CENTERED} marginless>
                                    {p.brand}
                                </Title>
                                <Title level={TitleLevels.FIVE} typo={TypographyAlign.TEXT_CENTERED}>
                                    {p.name}
                                </Title>
                            </Row>
                            <Row narrow>
                                <ImageThumbor
                                    src={p.image}
                                    alt=''
                                    width={60}
                                    height={80}
                                    position={0}
                                    fetchPriority='high'
                                />
                            </Row>
                            <Row narrow className='is-flex is-justified-center'>
                                <Price
                                    level={PriceLevel.SIX}
                                    amount={p.price}
                                    className='is-hidden-tablet is-marginless'
                                />
                                <Price level={PriceLevel.FOUR} amount={p.price} className='is-hidden-mobile' />
                            </Row>
                            <Row narrow>
                                <LinkTrilogy href={paths.phoneDetail(p.urlKey, isRenewal)} routerLink={Link}>
                                    Voir la fiche produit
                                </LinkTrilogy>
                            </Row>
                        </Rows>
                    </ComparatorCell>
                )),
                ...Array(Math.max(0, 3 - products.length))
                    .fill(null)
                    .map((_, index) => (
                        <ComparatorCell key={`placeholder-${index}`}>
                            <View
                                flexable
                                className='is-flex-col is-fullheight'
                                align={Align.CENTER}
                                justify={Justify.SPACE_BETWEEN}
                            >
                                <Title level={TitleLevels.FIVE} />
                                <Image
                                    src={`${appConfig.assets.cmsImages.url}/original/image/CMS/ecommerce/wall/lazyload-phone-default-image.svg`}
                                    alt=''
                                    width={60}
                                    height={80}
                                    fetchPriority='high'
                                />
                                <Button
                                    variant={ButtonVariant.PRIMARY}
                                    href={isRenewal ? paths.renewalPhoneWall : paths.phoneWall}
                                    routerLink={Link}
                                    accessibilityLabel='Ajouter un téléphone'
                                    onClick={() => sendBackToPreviousPageTracking()}
                                >
                                    Ajouter<span className='is-hidden-mobile'>&nbsp;un téléphone</span>
                                </Button>
                            </View>
                        </ComparatorCell>
                    )),
            ],
        },
        {
            name: 'Modèles',
            characteristics: [
                {
                    name: 'Couleurs disponibles',
                    values: products.map((p) => (
                        <View
                            flexable
                            align={Align.CENTER}
                            className='gap-small is-flex-wrap is-justified-start-mobile is-justified-center-tablet'
                            key={p.gencode + '-color'}
                        >
                            {p.colors.map((color) => (
                                <div key={p.gencode + '-' + color}>
                                    <div className='is-hidden-tablet'>
                                        <Color color={color} shadowless size={ColorSize.SMALL} />
                                    </div>
                                    <div className='is-hidden-mobile'>
                                        <Color color={color} shadowless size={ColorSize.MEDIUM} />
                                    </div>
                                </div>
                            ))}
                        </View>
                    )),
                },
                {
                    name: 'Mémoires disponibles',
                    values: products.map((p) => p.storage),
                },
            ],
        },
        {
            name: 'Chargeur secteur',
            characteristics: [
                {
                    name: 'Tête de charge incluse',
                    values: products.map((p) => p.chargingHead),
                },
                {
                    name: 'Puissance minimal requise',
                    values: products.map((p) => p.chargingMinPower),
                },
                {
                    name: 'Puissance maximale supportée',
                    values: products.map((p) => p.chargingMaxPower),
                },
            ],
        },
        {
            name: 'Étiquette énergétique',
            characteristics: [
                {
                    name: 'Classe énergétique',
                    values: products.map((p) =>
                        p.energyLabel ? (
                            <EnergyLabel key={`${p.gencode}-energy`} rating={p.energyLabel} className='m-auto' />
                        ) : undefined,
                    ),
                },
            ],
        },
        {
            name: 'Indice de réparabilité',
            characteristics: [
                {
                    name: 'Score réparabilité',
                    values: products.map((p) =>
                        p.reparability > 0 ? (
                            <View flexable className='is-centered-tablet' key={`${p.gencode}-reparability`}>
                                <ReparabilityNote value={p.reparability} />
                            </View>
                        ) : undefined,
                    ),
                },
            ],
        },
        {
            name: 'OS',
            characteristics: [
                {
                    name: 'Système',
                    values: products.map((p) => p.os),
                },
                {
                    name: "Version de l'OS",
                    values: products.map((p) => p.osVersion),
                },
            ],
        },
        {
            name: 'Réseau',
            characteristics: [
                {
                    name: 'Connectivité max',
                    values: products.map((p) => p.connectivity),
                },
                {
                    name: 'WiFi',
                    values: products.map((p) => p.wifi),
                },
            ],
        },
        {
            name: 'Carte SIM',
            characteristics: [
                {
                    name: 'Multi SIM',
                    values: products.map((p) => p.multipleSim),
                },
                {
                    name: 'Compatibilité eSIM',
                    values: products.map((p) => p.eSim),
                },
            ],
        },
        {
            name: 'Écran',
            characteristics: [
                {
                    name: 'Écran',
                    values: products.map((p) => p.screenSize),
                },
                {
                    name: "Largeur de l'écran",
                    values: products.map((p) => p.screenWidth),
                },
                {
                    name: "Hauteur de l'écran",
                    values: products.map((p) => p.screenHeight),
                },
            ],
        },
        {
            name: 'Photo',
            characteristics: [
                {
                    name: 'Photo',
                    values: products.map((p) => p.resolutionSensor),
                },
                {
                    name: 'Auto-focus',
                    values: products.map((p) => p.autoFocus),
                },
                {
                    name: 'Double caméra',
                    values: products.map((p) => p.doubleCamera),
                },
            ],
        },
        {
            name: 'Batterie',
            characteristics: [
                {
                    name: 'Capacité de la batterie',
                    values: products.map((p) => p.batteryCapacity),
                },
                {
                    name: 'Batterie Amovible',
                    values: products.map((p) => p.batteryRemovable),
                },
                {
                    name: 'Autonomie en communication',
                    values: products.map((p) => p.batteryTalkTime),
                },
                {
                    name: 'Autonomie',
                    values: products.map((p) => p.batteryStandbyTime),
                },
            ],
        },
        {
            name: 'Processeur',
            characteristics: [
                {
                    name: 'Processeur nombre de coeur',
                    values: products.map((p) => p.processorCore),
                },
                {
                    name: 'Vitesse du processeur',
                    values: products.map((p) => p.processorSpeed),
                },
            ],
        },
        {
            name: 'Encombrement',
            characteristics: [
                {
                    name: 'Poids',
                    values: products.map((p) => p.weight),
                },
                {
                    name: 'Longueur',
                    values: products.map((p) => p.length),
                },
                {
                    name: 'Largeur',
                    values: products.map((p) => p.width),
                },
                {
                    name: 'Hauteur',
                    values: products.map((p) => p.height),
                },
            ],
        },
        {
            name: 'DAS',
            characteristics: [
                {
                    name: 'Tête',
                    values: products.map((p) => p.sar),
                },
                {
                    name: 'Tronc',
                    values: products.map((p) => p.trunkSar),
                },
                {
                    name: 'Membres',
                    values: products.map((p) => p.limbsSar),
                },
            ],
        },
    ];
}
